import { useState, useRef, useCallback } from 'react'
import { useKonamiCode } from '@hooks/useKonamiCode'
import { useSecretClick } from '@hooks/useSecretClick'
import { useScrolledNav } from '@hooks/useScrolledNav'
import { useHighContrast } from '@hooks/useHighContrast'
import { Nav } from '@sections/Nav'
import { Hero } from '@sections/Hero'
import { About } from '@sections/About'
import { StorySection } from '@sections/StorySection'
import { ValuesSection } from '@sections/ValuesSection'
import { Projects } from '@sections/Projects'
import { Skills } from '@sections/Skills'
import { ExperienceSection } from '@sections/ExperienceSection'
import { Writing } from '@sections/Writing'
import { Contact } from '@sections/Contact'
import { ArticleView } from '@sections/ArticleView'
import { ArchiveView } from '@sections/ArchiveView'
import type { BlogPost } from '@sections/Writing'

interface ModernPortfolioProps {
  onEnterTerminal: () => void
}

export default function ModernPortfolio({ onEnterTerminal }: ModernPortfolioProps) {
  const scrolled = useScrolledNav()
  const { highContrast, toggle: toggleContrast } = useHighContrast()
  const footerDotRef = useRef<HTMLSpanElement>(null)
  const [activePost, setActivePost] = useState<BlogPost | null>(null)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const contrastAttr = highContrast ? 'high' : 'normal'

  const enterTerminal = useCallback(() => {
    onEnterTerminal()
  }, [onEnterTerminal])

  useKonamiCode(enterTerminal)
  useSecretClick(footerDotRef, enterTerminal)

  if (activePost) {
    return (
      <div className="modern-mode" data-modern-contrast={contrastAttr}>
        <ArticleView post={activePost} onBack={() => setActivePost(null)} />
      </div>
    )
  }

  if (archiveOpen) {
    return (
      <div className="modern-mode" data-modern-contrast={contrastAttr}>
        <ArchiveView onBack={() => setArchiveOpen(false)} />
      </div>
    )
  }

  return (
    <div className="modern-mode" data-modern-contrast={contrastAttr}>
      <Nav scrolled={scrolled} highContrast={highContrast} onToggleContrast={toggleContrast} />
      <Hero />
      <About />
      <StorySection onReadStory={setActivePost} />
      <ValuesSection />
      <Projects onOpenArchive={() => setArchiveOpen(true)} />
      <Skills />
      <ExperienceSection />
      <Writing onPostClick={setActivePost} />
      <Contact />
      <footer className="modern-footer">
        Built with React + TypeScript
        <span ref={footerDotRef} className="modern-footer__dot">.</span>
      </footer>
    </div>
  )
}
